/**
 * SciChart initialization — must run once, before any SciChartSurface is created.
 *
 * 1. Applies the runtime license key from VITE_SCICHART_KEY (see .env.example).
 *    Without it, charts render in community mode with a watermark.
 * 2. Points SciChart at the self-hosted WASM engine in public/ (copied there by
 *    scripts/copy-wasm.mjs), so there is no runtime CDN dependency and it works
 *    offline.
 * 3. Publishes a **verdict** (`chartsLicensed`) that anything about to construct
 *    a surface must await. In production a licence that does not hold means no
 *    surface at all — see `degraded` and `chartsLicensed` below.
 *
 * Import this module for its side effect *first* in the app entry (main.tsx),
 * before anything that touches SciChart.
 */

import { SciChartSurface, licenseManager2dState } from "scichart";

/**
 * Whether this origin is one the runtime key covers.
 *
 * The key is **domain-locked**, and the lock is on the host string rather than
 * on anything routable: `localhost` is licensed and `127.0.0.1` is not, though
 * they are the same machine. A LAN address is never on it — a phone reaches
 * this dev server as `10.0.0.220`, and DHCP will hand out a different number
 * next week, so registering one is not a fix either.
 *
 * Applying the key off-domain is **worse than not applying it**: SciChart
 * refuses and draws its licence notice *instead of the chart*, where community
 * mode draws the chart with a watermark. A watermark is a nuisance; a chart
 * that will not render is a device you cannot test on. So the key is used where
 * it is valid and stood down where it is not.
 *
 * Production is licensed by its real domain, so anything that is not a bare IP
 * gets the key — a deployed host must never silently fall to community mode.
 */
export function licensedOrigin(hostname: string): boolean {
  return !isIpLiteral(hostname);
}

/**
 * Whether a host string is an IP address rather than a name.
 *
 * **v6 as well as v4**, which is the half this missed. A browser gives an IPv6
 * host bracketed — `location.hostname` on `http://[::1]:5173/` is `[::1]` — and
 * a bracketed address matches nothing in a dotted-quad pattern, so it read as a
 * *named* host and was handed the key. That is the failure this whole predicate
 * exists to prevent: the key is refused and SciChart draws its licence notice
 * instead of the chart.
 *
 * Unbracketed `::1` is accepted too. No browser reports it that way, but this is
 * called with whatever a caller has, and the cost of being wrong is asymmetric —
 * a watermark against a chart that does not draw.
 */
export function isIpLiteral(hostname: string): boolean {
  if (!hostname) return false;
  // Bracketed is how a URL carries v6, so the brackets are the reliable tell
  // and the address inside does not need parsing.
  if (hostname.startsWith("[") && hostname.endsWith("]")) return true;
  // Bare v6 is not a browser's spelling, but a colon can appear in no hostname
  // — a port never reaches `location.hostname` — so one is enough to decide.
  if (hostname.includes(":")) return true;
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
}

/**
 * Which licence to apply, as a decision rather than a side effect.
 *
 * Extracted because the bug it now pins was **a missing branch**, and a missing
 * branch in a function that calls into SciChart is unreachable from a test —
 * this module cannot be imported under jsdom. As four values it is ordinary
 * logic, and "every path that is not `runtime` is stated" is a thing a test can
 * assert.
 */
export type LicenseMode =
  | { apply: "runtime" }
  | { apply: "community"; why: "off-domain" | "no-key" | "asked" }
  | { apply: "blocked"; why: "off-domain" | "no-key" };

/**
 * @param forceCommunity stand the key down even where it would be applied.
 * @param production this is a built bundle, where a degraded chart is not
 *   acceptable at all. See `degraded`.
 *
 * The escape hatch for the case the host string cannot describe: **a browser
 * with no developer licence**. On `localhost` the key is honoured only when the
 * Licensing Wizard has activated *this browser* — the licence it issues is
 * cached per browser profile — so the same key, the same URL and the same
 * machine can render clean in one browser and draw the licence notice in
 * another, which is precisely what happened in Edge beside a working
 * DuckDuckGo. Nothing available at init distinguishes the two: the wizard lives
 * behind an async call to `localhost:24278` and this function is synchronous.
 *
 * So it is a declaration rather than a detection, and the default is unchanged —
 * a developer who has the wizard is not asked to do anything.
 */
export function licenseMode(
  key: string,
  hostname: string,
  forceCommunity = false,
  production = false,
): LicenseMode {
  // `no-key` outranks `asked` because both end in the same place and only the
  // message differs: a missing key is a setup step, a request for community mode
  // is a choice, and the setup step is the more useful thing to be told about.
  if (!key) return degraded(production, "no-key");
  //
  // **`asked` stays community even in production, and that is deliberate.**
  //
  // A preview build is `vite build`, so `import.meta.env.PROD` is true for it —
  // gating on production alone would *block* every preview rather than
  // watermarking it, which is the opposite of what previews are for. This flag
  // is the declaration that a watermark is wanted, and a declaration outranks an
  // inference drawn from the build mode. `docs/plans/web-deploy.md` records the
  // reading it rests on: previews are development for this purpose.
  //
  if (forceCommunity) return { apply: "community", why: "asked" };
  return licensedOrigin(hostname) ? { apply: "runtime" } : degraded(production, "off-domain");
}

/**
 * What a non-runtime outcome costs, which is the 2026-09-03 requirement in one
 * function.
 *
 * In development it is a watermark, and that is useful — it says the licence is
 * not being applied without hiding the chart it would have drawn. In production
 * it is nothing: no licence is applied, no surface is constructed, and the app
 * puts its own message where the chart would be. A user must never read
 * SciChart's licence notice and must never see its watermark. Both are ours to
 * explain, in our own words.
 */
function degraded(production: boolean, why: "off-domain" | "no-key"): LicenseMode {
  return production ? { apply: "blocked", why } : { apply: "community", why };
}

/**
 * SciChart's own view of whether the licence held up.
 *
 * **Mirrored, because the enums are not exported.** `licenseManager2dState` is
 * on the package root (`index.d.ts`), but `LicenseCheckStatus` and `LicenseType`
 * live in `types/licensingClasses` marked `@Ignore` and are not re-exported. So
 * the numbers are written out here, and `scichart-bootstrap.test.ts` imports the
 * real enums and asserts these against them — a version bump that renumbers one
 * fails a test rather than silently blocking every chart in production.
 */
export const LICENSE_CHECK = {
  NoLicense: 0,
  FetchingFromServer: 1,
  StartLookingForLicenseWizard: 2,
  LookingForLicenseWizard: 3,
  ValidatingDeveloperLicense: 4,
  NoLicenseInWizard: 5,
  FailedToFindLicenseWizard: 6,
  FailedToValidateDeveloperLicense: 7,
  DevKeyInRuntimeKey: 8,
  LicenseOK: 9,
  ValidatingRuntimeKey: 10,
  FailedToValidateRuntimeKey: 11,
} as const;

/**
 * The licence *kind*, which is a separate question from whether the check
 * completed — and the one that decides whether a watermark is drawn.
 *
 * Mirrored from `types/licensingClasses` for `LICENSE_CHECK`'s reason, and
 * pinned against the real enum by the same test.
 */
export const LICENSE_TYPE = {
  NoLicense: 0,
  Trial: 1,
  Full: 2,
  Full_Expired: 3,
  Trial_Expired: 4,
  Subscription_Expired: 5,
  Invalid_Developer: 6,
  Requres_Validation: 7,
  Invalid: 8,
  Community: 9,
} as const;

/**
 * Whether this licence kind draws SciChart's watermark — i.e. anything but Full.
 *
 * **Allow-list, not deny-list, and that reversal was measured.** The first
 * version denied `Community` only, on the reasoning that expired and invalid
 * kinds never reach `LicenseOK` and naming them would be guessing. The live site
 * then drew a watermarked chart anyway: the debug dump reported
 * `License Type: Full` with `Full Features:` **empty** and `JS-2D` under
 * `Trial Features`, so the entitlement was a trial while the licence called
 * itself valid.
 *
 * Nothing on `licenseManager2dState` exposes the feature list, so the kind is
 * all there is to read — and a deny-list can only ever catch the failures
 * somebody has already seen. `Full` is the one state that is certainly not
 * watermarked, so it is the one state allowed.
 *
 * The cost is stated plainly: an unexpected value withholds every chart. That is
 * the trade the requirement asks for — no watermark in front of a user, ever —
 * and the console line beside it names the value so the cause is never a guess.
 */
export function isWatermarked(licenseType: unknown): boolean {
  return licenseType !== LICENSE_TYPE.Full;
}

/**
 * The states that mean "still deciding", so a reader waits rather than judging.
 *
 * The check is **asynchronous** — it fetches from a server, or talks to the
 * Licensing Wizard on `localhost:24278` — so reading the verdict straight after
 * `setRuntimeLicenseKey` reads a value that means nothing yet.
 *
 * `NoLicense` (0) is in here and that is the load-bearing entry: it is also the
 * *initial* value of the field, so a synchronous read sees it before anything
 * has happened. Treating it as a settled failure would block every chart on
 * every load; treating it as in-flight costs at most one timeout in the genuine
 * no-licence case, which reaches the same verdict by a slower road.
 */
const IN_FLIGHT: readonly number[] = [
  LICENSE_CHECK.NoLicense,
  LICENSE_CHECK.FetchingFromServer,
  LICENSE_CHECK.StartLookingForLicenseWizard,
  LICENSE_CHECK.LookingForLicenseWizard,
  LICENSE_CHECK.ValidatingDeveloperLicense,
  LICENSE_CHECK.ValidatingRuntimeKey,
];

/**
 * Whether SciChart has finished deciding. Pure, so it is testable.
 *
 * **A non-number is "not started", not "settled".** `LicenseManager2dState`'s
 * constructor initialises `isDev`, `orderId`, `licenseType` and the rest — and
 * *not* `checkStatus`, which is therefore `undefined` until the licence flow
 * writes it. Reading `undefined` as a settled non-OK value is how the first
 * deployed build reached the right verdict for the wrong reason: it never waited
 * at all, and a *valid* licence would have been refused the same way.
 */
export function isLicenseSettled(checkStatus: unknown): boolean {
  if (typeof checkStatus !== "number") return false;
  return !IN_FLIGHT.includes(checkStatus);
}

/**
 * Whether charts may be drawn, and why not when they may not.
 *
 * Separate from `LicenseMode` because the two answer different questions at
 * different times: the mode is decided synchronously from the key and the host,
 * before SciChart is told anything, and this is the answer *after* SciChart has
 * had its say. Collapsing them would make the pure, tested half asynchronous.
 */
export type ChartsVerdict =
  | { charts: "allowed"; how: "runtime" | "community" }
  | { charts: "blocked"; why: "off-domain" | "no-key" | "refused" | "timeout" };

/** How long to wait for SciChart to settle before calling it a failure. */
const SETTLE_TIMEOUT_MS = 10_000;
const SETTLE_POLL_MS = 100;

/**
 * The pre-flight decision, recorded by `initSciChart` for `chartsLicensed` to
 * finish. Held rather than resolved eagerly — see the note on laziness below.
 */
let applied: LicenseMode | null = null;
let isProduction = false;
let gate: Promise<ChartsVerdict> | null = null;

/**
 * The verdict, for anything that has just constructed a surface.
 *
 * ## Why this is lazy, and why it does not gate construction
 *
 * The first design awaited this *before* building a surface, on the reasoning
 * that SciChart draws its licence notice at construction and so the only safe
 * move is not to construct. That reasoning is right and the mechanism deadlocks,
 * which the first production deploy proved:
 *
 * `setRuntimeLicenseKey` calls `applyLicense` over `getAllLicenseContexts()`,
 * and that reads `sciChartDestinations` — **the surfaces**. With no surface the
 * list is empty, nothing is applied, and `licenseManager2dState.checkStatus` is
 * never written. It is not initialised by the state object's constructor either,
 * so it stays `undefined` forever. A gate that waits for it before allowing a
 * surface waits for something only a surface can cause.
 *
 * So the surface is built, and what is withheld is **the sight of it**: the
 * caller keeps an opaque cover over the container until this resolves, then
 * reveals the chart or replaces it with our own message. The user still never
 * sees SciChart's notice or its watermark, which is the actual requirement — the
 * cover is committed by React in the same render as the container, and the
 * surface is only created in an effect that runs after paint.
 *
 * Lazy for the same reason: the poll must not start until a caller exists, since
 * a caller existing is what makes the licence flow run.
 */
export function chartsLicensed(): Promise<ChartsVerdict> {
  if (!gate) gate = decide();
  return gate;
}

async function decide(): Promise<ChartsVerdict> {
  // Before `initSciChart` has run there is nothing to report. `main.tsx` calls it
  // first, so this is a test or a misordered import rather than a real state, and
  // the old behaviour — draw, whatever happens — is right for both.
  if (!applied) return { charts: "allowed", how: "community" };
  if (applied.apply === "blocked") return { charts: "blocked", why: applied.why };
  if (applied.apply === "community") return { charts: "allowed", how: "community" };
  // Development draws whatever SciChart decides: a watermark is informative
  // there, and a wait to be told so is not.
  if (!isProduction) return { charts: "allowed", how: "runtime" };
  return settleVerdict();
}

async function settleVerdict(): Promise<ChartsVerdict> {
  const deadline = Date.now() + SETTLE_TIMEOUT_MS;
  for (;;) {
    const status = licenseManager2dState.checkStatus as unknown as number;
    if (isLicenseSettled(status)) {
      const kind = licenseManager2dState.licenseType as unknown as number;
      // Always stated, not only on failure. "Which licence is this actually
      // running on?" was unanswerable from the deployed site, and the watermark
      // was the only evidence — which is a poor instrument, since it is what the
      // gate exists to prevent anyone seeing.
      // eslint-disable-next-line no-console
      console.info(`[scichart] licence settled: checkStatus=${status} licenseType=${kind}`);
      if (status === LICENSE_CHECK.LicenseOK && !isWatermarked(kind)) {
        return { charts: "allowed", how: "runtime" };
      }
      if (isWatermarked(kind)) {
        // eslint-disable-next-line no-console
        console.error(
          `[scichart] licenseType=${kind} is not Full, so SciChart would draw its ` +
            "watermark. Charts are withheld instead. Run " +
            "`licenseManager.setIsDebugLicensing(true)` and reload for the vendor's " +
            "own dump — check whether JS-2D is under Full Features or Trial Features.",
        );
        return { charts: "blocked", why: "refused" };
      }
      const detail = licenseManager2dState.error ? `: ${licenseManager2dState.error}` : "";
      // eslint-disable-next-line no-console
      console.error(
        `[scichart] the runtime licence was refused (checkStatus ${status}${detail}). ` +
          "Charts are withheld rather than drawn with SciChart's licence notice.",
      );
      return { charts: "blocked", why: "refused" };
    }
    if (Date.now() >= deadline) {
      // **A timeout is a failure, not a pass.** The alternative is that a slow or
      // blocked validation call becomes a watermark in production, which is the
      // state this whole gate exists to make unreachable.
      // eslint-disable-next-line no-console
      console.error(
        `[scichart] licence validation did not settle in ${SETTLE_TIMEOUT_MS}ms ` +
          `(checkStatus ${licenseManager2dState.checkStatus}). Charts are withheld.`,
      );
      return { charts: "blocked", why: "timeout" };
    }
    await new Promise((resolve) => setTimeout(resolve, SETTLE_POLL_MS));
  }
}

export function initSciChart(): void {
  const key = import.meta.env.VITE_SCICHART_KEY ?? "";
  const host = globalThis.location?.hostname ?? "";
  const production = import.meta.env.PROD;
  //
  // **Community mode has to be declared, not merely fallen into.** There are
  // three ways to end up without a key — off-domain, no key at all, and asked
  // for — and once only the first said so. The second logged a line and
  // returned, which leaves SciChart
  // with no licence *and no instruction*, so it goes looking for a developer one
  // through the License Wizard on `localhost:24278`. With the wizard not
  // running that is `ERR_CONNECTION_REFUSED` and "Failed to connect to license
  // wizard/api/license", and the charts do not draw.
  //
  // So the shape is: apply the key only where it is valid, and in every other
  // case say community explicitly. The messages differ because the situations
  // do — one is a deliberate fallback on a LAN address, the other is a missing
  // setup step — but neither may skip the declaration.
  //
  // Production adds a fourth outcome, `blocked`: no licence is applied and no
  // surface is built, because a user must see neither SciChart's notice nor its
  // watermark. See `degraded` above.
  const mode = licenseMode(key, host, import.meta.env.VITE_SCICHART_COMMUNITY === "1", production);
  applied = mode;
  isProduction = production;

  if (mode.apply === "blocked") {
    // Nothing is told to SciChart at all — not the key, which would be refused,
    // and not community, which would draw a watermark.
    // eslint-disable-next-line no-console
    console.error(
      mode.why === "no-key"
        ? "[scichart] VITE_SCICHART_KEY is not set in a production build. Charts are withheld."
        : `[scichart] ${host} is not a licensable host (it is an IP address). Charts are withheld.`,
    );
  } else if (mode.apply === "runtime") {
    SciChartSurface.setRuntimeLicenseKey(key);
  } else if (mode.why === "asked") {
    // Deliberate, so it is stated rather than warned about: a browser without a
    // developer licence draws a watermarked chart instead of a licence notice.
    //
    // **This wins over the production gate**, which is what keeps preview builds
    // usable: a preview is `vite build`, so it is a production build by every
    // test available here, and blocking it would be the opposite of the point.
    // eslint-disable-next-line no-console
    console.info(
      "[scichart] VITE_SCICHART_COMMUNITY=1 — community mode with a watermark, by request. " +
        "Unset it once the Licensing Wizard has activated this browser.",
    );
    SciChartSurface.UseCommunityLicense();
  } else {
    if (mode.why === "off-domain") {
      // Reached by a phone on the LAN. Say so once — a watermark with no
      // explanation reads as a broken licence rather than a deliberate fallback.
      // eslint-disable-next-line no-console
      console.info(
        `[scichart] ${host} is not on the runtime licence (it is domain-locked), ` +
          "so this origin renders in community mode with a watermark. " +
          "localhost and the production domain are unaffected.",
      );
    } else if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        "[scichart] VITE_SCICHART_KEY not set — rendering in community mode (watermark). " +
          "Copy ts/apps/web/.env.example to .env.local and add your key.",
      );
    }
    SciChartSurface.UseCommunityLicense();
  }

  // Self-hosted WASM (no CDN). Files are served from /public by copy-wasm.mjs.
  // v5 embeds the old .data payload in the .wasm; ship a no-SIMD fallback too.
  SciChartSurface.configure({
    wasmUrl: "/scichart2d.wasm",
    wasmNoSimdUrl: "/scichart2d-nosimd.wasm",
  });
}
